import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import moment from 'moment';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import Paper from '@mui/material/Paper';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

// Utils
import { Storage } from '../../../utils/Storage';
import { useExport } from '../../../hooks/useExport';
import { FormatForApi, shortDateFormat } from '../../../utils/Format';
import { makeCollectionListColumn } from '../../../utils/columns/makeCollectionListColumn';

// Services
import { getCollections } from '../../../services/collections.service';

// Types
import { FilterableTypes } from '../../../types/FilterableTypes';
import { CollectionMetaAndDetails, SearchBarFilterTypes } from '../../../constants/Constants';

// Commmon
import SearchBar from '../../common/SearchBar/SearchBar';
import { useStandardTable as useTable, Table } from '../../common/table/index';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';

const Collections = () => {
  const navigate = useNavigate();

  const [lastSeenDateTimeFrom, setLastSeenDateTimeFrom] = useState<Date | null>(null);
  const [lastSeenDateTimeTo, setLastSeenDateTimeTo] = useState<Date | null>(null);
  const [paramFilter, setParamFilter] = useState<any>({
    entityType: 'StroopwaflCollection',
    filters: [],
  });
  const [isResetting, setIsResetting] = useState(false);

  const { isExporting, startExport } = useExport('StroopwaflCollection');

  useEffect(() => {
    if (isResetting) {
      // Reset paramFilter and clear the resetting flag
      setParamFilter({ entityType: 'StroopwaflCollection', filters: [], sortBy: paramFilter.sortBy });
      setIsResetting(false);
    }
  }, [isResetting, paramFilter.sortBy]);

  useEffect(() => {
    const storedFilter = Storage.getSearchBarFiltering(SearchBarFilterTypes.COLLECTIONS);
    if (storedFilter?.filters?.length) {
      setParamFilter({
        entityType: 'StroopwaflCollection',
        filters: storedFilter.filters,
        sortBy: paramFilter.sortBy,
      });
    }
  }, []);

  const handleReset = () => {
    setIsResetting(true);
    setLastSeenDateTimeFrom(null);
    setLastSeenDateTimeTo(null);
    setParamFilter({ entityType: 'StroopwaflCollection', filters: [], sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.COLLECTIONS, { filters: [] });
  };

  const onInfoClick = (event: any, collectionDetails: any) => {
    if (event.ctrlKey === true) window.open(`/collections/${collectionDetails.id}`);
    else navigate(`/collections/${collectionDetails.id}`, { state: collectionDetails });
  };

  const columns = useMemo(() => makeCollectionListColumn(onInfoClick), []);

  const table = useTable<any>({
    columns,
    fetchAsync: getCollections,
    params: paramFilter,
    serverSide: true,
    shouldFetch: true,
  });

  const onSearchChange = (filters: any) => {
    if (lastSeenDateTimeFrom && lastSeenDateTimeTo) {
      filters.push({
        createdDateTime: FormatForApi(moment(lastSeenDateTimeFrom), true),
        filterType: FilterableTypes.GreaterThanOrEqual,
        and: {
          createdDateTime: FormatForApi(moment(lastSeenDateTimeTo), false),
          filterType: FilterableTypes.LessThanOrEqual,
        },
      });
    }
    setParamFilter({ entityType: 'StroopwaflCollection', filters: filters });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.COLLECTIONS, { isMoreFilter: true, filters: filters });
  };

  return (
    <>
      <SearchBar
        pageKey={SearchBarFilterTypes.COLLECTIONS}
        onSearch={onSearchChange}
        onReset={handleReset}
        columns={columns}
        allowSearchAll={true}
        components={
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <FormInputCollection label={`${CollectionMetaAndDetails.createdDate}`} style={{ marginBottom: '8px' }}>
              <div style={{ marginRight: '8px' }}>
                <DatePicker
                  label='From'
                  value={lastSeenDateTimeFrom ? moment(lastSeenDateTimeFrom) : null}
                  format={shortDateFormat}
                  sx={{ width: '185px' }}
                  slotProps={{ textField: { size: 'small' } }}
                  onChange={(date) => setLastSeenDateTimeFrom(date ? date.toDate() : null)}
                />
              </div>
              <DatePicker
                value={lastSeenDateTimeTo ? moment(lastSeenDateTimeTo) : null}
                label='To'
                format={shortDateFormat}
                sx={{ width: '185px' }}
                slotProps={{ textField: { size: 'small' } }}
                onChange={(date) => setLastSeenDateTimeTo(date ? date.toDate() : null)}
              />
            </FormInputCollection>
          </LocalizationProvider>
        }
      />
      <Paper>
        <Table
          {...table}
          isExporting={isExporting}
          canExportData={() => true}
          exportData={() => startExport(paramFilter.filters ?? [])}
          showSearchBar={false}
          showColumnToggler={false}
          getCoreRowModel={getCoreRowModel()}
        />
      </Paper>
    </>
  );
};

export default Collections;
